import { useRef } from 'react';
import { t } from '../../i18n/uiText';
import {
  afterReaderSelectionSettled,
  hasReaderTextSelection,
  isVerseTextPointerTarget,
  looksLikeTextSelectionGesture,
} from '../../utils/readerTextSelection';

const TAP_SLOP_PX = 10;

export default function VerseCard({
  verse,
  onVerseTap,
  isActive,
  showInlineActions = false,
  onOpenInsight,
  onOpenSimple,
  onOpenApplication,
  onOpenKeyIdea,
  moreRef,
  moreOpen = false,
  onToggleMore,
  moreDirection = 'up',
  moreAlign = 'right',
  onOpenInterpretation,
  onOpenParallels,
  onShare,
  isLoadingParallel = false,
}) {
  const pointerStart = useRef(null);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (hasReaderTextSelection()) return;
    const fromText = isVerseTextPointerTarget(e.target);
    pointerStart.current = {
      fromText,
      x: e.clientX,
      y: e.clientY,
      id: e.pointerId,
      t0: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const clearPointer = () => {
    pointerStart.current = null;
  };

  const onPointerUp = (e) => {
    const start = pointerStart.current;
    clearPointer();
    if (!start || e.pointerId !== start.id) return;

    const durationMs =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - (start.t0 || 0);
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (
      hasReaderTextSelection() ||
      looksLikeTextSelectionGesture(start.x, start.y, e.clientX, e.clientY, durationMs)
    ) {
      return;
    }
    if (dx <= TAP_SLOP_PX && dy <= TAP_SLOP_PX) {
      afterReaderSelectionSettled(() => {
        if (hasReaderTextSelection()) return;
        onVerseTap?.(verse);
      });
    }
  };

  const onPointerCancel = () => {
    clearPointer();
  };

  return (
    <div
      className={`bible-verse ${isActive ? 'active' : ''}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerCancel}
      aria-label={isActive ? `${t('verse')} ${verse.verse}` : undefined}
    >
      <sup className="verse-number-sup" aria-hidden="true">
        {verse.verse}
      </sup>
      <span className="verse-text">{verse.text}</span>
      {showInlineActions && (
        <div
          className="verse-inline-actions verse-inline-actions--in-card"
          role="group"
          aria-label={t('verseActions')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="verse-inline-action-btn verse-inline-action-btn-primary"
            onClick={() => onOpenInsight?.()}
          >
            {'\u{1F4A1}'} {t('verseInsightButton')}
          </button>
          <div
            ref={moreRef}
            className={`verse-inline-more ${moreOpen ? 'is-open' : ''}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="verse-inline-action-btn verse-inline-action-btn-more"
              aria-expanded={moreOpen}
              aria-label={t('moreActions')}
              title={t('moreActions')}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleMore?.();
              }}
            >
              ⋯
            </button>
            {moreOpen && (
              <div
                className={`verse-inline-more-menu verse-inline-more-menu--${moreDirection} verse-inline-more-menu--${moreAlign}`}
              >
                <button
                  type="button"
                  className="verse-inline-more-item"
                  onClick={() => onOpenKeyIdea?.()}
                >
                  💡 {t('chatFollowUpKeyThought')}
                </button>
                <button
                  type="button"
                  className="verse-inline-more-item"
                  onClick={() => onOpenInterpretation?.()}
                >
                  ✨ {t('interpretation')}
                </button>
                <button
                  type="button"
                  className="verse-inline-more-item"
                  disabled={isLoadingParallel}
                  onClick={() => onOpenParallels?.()}
                >
                  📖 {t('parallels')}
                </button>
                <button
                  type="button"
                  className="verse-inline-more-item"
                  onClick={() => onShare?.()}
                >
                  📤 {t('share')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
